// frontend/components/sidebar/ContextMenu.tsx

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Edit2, Copy, Trash2, Star, StarOff, EyeOff, Link2,
  ArrowRightLeft, Users, ArrowUpRight, PanelRight, UserMinus
} from "lucide-react";

type PermLevel = "FULL" | "EDIT" | "COMMENT" | "READ";

interface ContextMenuProps {
  contextMenu: {
    visible: boolean;
    x: number;
    y: number;
    docId?: string | null;
    title?: string;
    icon?: string | null;
    isTrueOwner?: boolean;
    section?: "recent" | "favorite" | "team" | "personal";
    isFavorite?: boolean;
    isRoot?: boolean;
    permLevel?: PermLevel | null;
    updatedBy?: string;
    updatedAt?: string;
  };
  onClose: () => void;
  onRename: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onToggleFavorite?: () => void;
  onRemoveFromRecent?: () => void;
  onCopyLink?: () => void;
  onMove?: () => void;
  onPermissionSettings?: () => void;
  onEndShare?: () => void;
  onOpenNewTab?: () => void;
  onOpenSideView?: () => void;
}

const LEVEL_LABEL: Record<PermLevel, string> = {
  FULL: "전체 허용",
  EDIT: "편집 허용",
  COMMENT: "댓글 허용",
  READ: "읽기 허용",
};

const Divider = () => <div className="alpha-sidebar-menu-divider" />;

function MenuItem({ icon: Icon, onClick, label, danger = false }: any) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      className={`alpha-sidebar-menu-item w-full flex items-center transition-colors rounded-sm mx-1 max-w-[calc(100%-8px)]
        ${danger
          ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-zinc-700'
        }`}
    >
      <Icon className={`alpha-sidebar-menu-icon shrink-0 ${danger ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`} />
      <span>{label}</span>
    </button>
  );
}

function DisabledMenuItem({ icon: Icon, label, permLevel, danger = false }: { icon: any; label: string; permLevel: PermLevel | null | undefined; danger?: boolean }) {
  const [hovered, setHovered] = useState(false);
  const levelLabel = permLevel ? LEVEL_LABEL[permLevel] : null;

  return (
    <div className="relative mx-1 max-w-[calc(100%-8px)]">
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="alpha-sidebar-menu-item w-full flex items-center rounded-sm cursor-not-allowed opacity-40"
      >
        <Icon className="alpha-sidebar-menu-icon shrink-0 text-gray-400 dark:text-gray-500" />
        <span className={danger ? "text-red-400 dark:text-red-600" : "text-gray-500 dark:text-gray-400"}>{label}</span>
      </div>
      {hovered && levelLabel && (
        <div className="absolute left-full top-0 ml-2 z-50 px-2.5 py-1.5 bg-gray-800 dark:bg-zinc-900 text-white text-[11px] rounded-md shadow-xl whitespace-nowrap pointer-events-none">
          {levelLabel} 권한으로 사용할 수 없는 기능입니다.
        </div>
      )}
    </div>
  );
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return "날짜 정보 없음";
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const h = date.getHours();
  const m = date.getMinutes();
  const ampm = h < 12 ? "오전" : "오후";
  const hh = h % 12 || 12;
  const mm = String(m).padStart(2, "0");
  const time = `${ampm} ${hh}:${mm}`;

  if (dateDay.getTime() === today.getTime()) return `오늘 ${time}`;
  if (dateDay.getTime() === yesterday.getTime()) return `어제 ${time}`;
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일 ${time}`;
}

export default function ContextMenu({
  contextMenu, onClose, onRename, onDelete, onDuplicate,
  onToggleFavorite, onRemoveFromRecent, onCopyLink, onMove,
  onPermissionSettings, onEndShare, onOpenNewTab, onOpenSideView
}: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [posStyle, setPosStyle] = useState<{ top: number; left: number; opacity?: number }>({
    top: contextMenu.y,
    left: contextMenu.x,
    opacity: 0
  });

  useEffect(() => {
    if (contextMenu.visible && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const winW = window.innerWidth;
      const winH = window.innerHeight;
      let x = contextMenu.x;
      let y = contextMenu.y;

      if (x + rect.width > winW) x = contextMenu.x - rect.width;
      if (y + rect.height > winH) y = contextMenu.y - rect.height;

      x = Math.max(8, x);
      y = Math.max(8, y);

      setPosStyle({ top: y, left: x });
    } else {
      setPosStyle({ top: contextMenu.y, left: contextMenu.x, opacity: 0 });
    }
  }, [contextMenu.visible, contextMenu.x, contextMenu.y]);

  if (!contextMenu.visible || typeof document === "undefined") return null;

  const { section, isFavorite, isRoot, isTrueOwner, permLevel } = contextMenu;

  const isRecent = section === 'recent';
  const isFavoriteMenu = section === 'favorite';
  const isTeamRoot = section === 'team' && isRoot;

  // 팀 문서에서 permLevel 기반 가능 여부 판별
  const canEdit = isTrueOwner || permLevel === 'FULL' || permLevel === 'EDIT';
  const canDelete = isTrueOwner || permLevel === 'FULL';
  const canPermSettings = isTrueOwner && isTeamRoot;

  // 항목 표시 대상 섹션
  const inEditableSection = isRecent || isTeamRoot || section === 'team' || section === 'personal';
  const inRenameSection = isRecent || isFavoriteMenu || isTeamRoot || section === 'team' || section === 'personal';

  return createPortal(
    <div
      ref={menuRef}
      data-sidebar-context-menu
      className="fixed z-[1000] w-56 bg-white dark:bg-[#202020] rounded-md shadow-2xl border border-gray-200 dark:border-zinc-700/60 py-1.5 animate-in fade-in zoom-in-95 duration-100"
      style={{ top: posStyle.top, left: posStyle.left, ...(posStyle.opacity !== undefined && { opacity: posStyle.opacity }) }}
      onClick={(e) => { e.stopPropagation(); onClose(); }}
    >
      <div className="px-3 py-1 mb-1 text-[11px] font-bold text-gray-400 dark:text-gray-500">페이지</div>

      <MenuItem icon={isFavorite ? StarOff : Star} label={isFavorite ? "즐겨찾기 해제" : "즐겨찾기에 추가"} onClick={onToggleFavorite} />
      {isRecent && <MenuItem icon={EyeOff} label="최근 문서에서 제거" onClick={onRemoveFromRecent} />}
      <Divider />

      <MenuItem icon={Link2} label="링크 복사" onClick={onCopyLink} />

      {/* 복제 */}
      {inEditableSection && (
        canEdit
          ? <MenuItem icon={Copy} label="복제" onClick={onDuplicate} />
          : <DisabledMenuItem icon={Copy} label="복제" permLevel={permLevel} />
      )}

      {/* 이름 바꾸기 / 옮기기 / 휴지통으로 이동 — 내가 만든(최초 작성팀=true owner) 문서일 때만.
          공유받은 문서(!isTrueOwner)는 이 셋을 아예 숨기고, 대신 "공유 종료"만 제공 */}
      {isTrueOwner && (
        <>
          {inRenameSection && (
            canEdit
              ? <MenuItem icon={Edit2} label="이름 바꾸기" onClick={onRename} />
              : <DisabledMenuItem icon={Edit2} label="이름 바꾸기" permLevel={permLevel} />
          )}

          {/* 옮기기 — EDIT 권한으로는 불가, FULL 권한이 있어야 가능 */}
          {inRenameSection && (
            canDelete
              ? <MenuItem icon={ArrowRightLeft} label="옮기기" onClick={onMove} />
              : <DisabledMenuItem icon={ArrowRightLeft} label="옮기기" permLevel={permLevel} />
          )}

          {/* 휴지통으로 이동 */}
          {inEditableSection && (
            canDelete
              ? <MenuItem icon={Trash2} label="휴지통으로 이동" danger onClick={onDelete} />
              : <DisabledMenuItem icon={Trash2} label="휴지통으로 이동" permLevel={permLevel} danger />
          )}
        </>
      )}

      <Divider />

      {/* 권한 설정 — 팀 루트 + true owner만. 공유받은(!isTrueOwner) 문서는 같은 자리에 "공유 종료" */}
      {canPermSettings && (
        <>
          <MenuItem icon={Users} label="권한 설정" onClick={onPermissionSettings} />
          <Divider />
        </>
      )}
      {!isTrueOwner && inRenameSection && (
        <>
          <MenuItem icon={UserMinus} label="공유 종료" danger onClick={onEndShare} />
          <Divider />
        </>
      )}

      <MenuItem icon={ArrowUpRight} label="새 탭에서 열기" onClick={onOpenNewTab} />
      <MenuItem icon={PanelRight} label="사이드 보기에서 열기" onClick={onOpenSideView} />
      <Divider />

      <div className="px-3 py-1.5 text-[11px] font-normal tracking-tight leading-relaxed text-gray-400 dark:text-zinc-500">
        <div>{contextMenu.updatedBy || "이름 없음"} 최종 편집</div>
        <div>{formatDate(contextMenu.updatedAt)}</div>
      </div>
    </div>,
    document.body
  );
}
